import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';

interface User {
  id: number;
  username: string;
  email?: string;
  isAdmin: number;
  createdAt: string;
  updatedAt?: string;
}

interface CreateUserData {
  username: string;
  password?: string;
  isAdmin: number;
}

export const useUsers = () => {
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.get<User[]>('/api/users');
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const createUser = useCallback(async (userData: CreateUserData) => {
    const result = await apiClient.post<User>('/api/users', userData);
    await fetchUsers();
    return result;
  }, [fetchUsers]);

  const updateUser = useCallback(async (id: number, userData: Partial<CreateUserData>) => {
    const result = await apiClient.put<User>(`/api/users/${id}`, userData);
    await fetchUsers();
    return result;
  }, [fetchUsers]);

  const deleteUser = useCallback(async (id: number) => {
    await apiClient.delete(`/api/users/${id}`);
    await fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    data,
    loading,
    error,
    refetch: fetchUsers,
    createUser,
    updateUser,
    deleteUser,
  };
};